import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, ArrowRight, AlertCircle, CheckCircle } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const handleReset = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent. Please check your inbox!");
    } catch (err) {
      console.error("Password reset error:", err);
      let message = "Failed to send password reset email.";
      if (err.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (err.code === "auth/invalid-email") {
        message = "Please enter a valid email address.";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      fontFamily: "'Inter', sans-serif"
    },
    card: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderRadius: "24px",
      padding: "48px",
      width: "100%",
      maxWidth: "440px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      backdropFilter: "blur(20px)"
    },
    title: {
      fontSize: "26px",
      fontWeight: 700,
      textAlign: "center",
      marginBottom: "8px",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent"
    },
    subtitle: {
      textAlign: "center",
      color: "#6b7280",
      marginBottom: "24px"
    },
    inputWrapper: {
      position: "relative",
      marginBottom: "24px"
    },
    input: {
      width: "100%",
      padding: "16px 16px 16px 48px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      backgroundColor: "#f9fafb",
      fontSize: "16px",
      outline: "none"
    },
    inputIcon: {
      position: "absolute",
      top: "50%",
      left: "16px",
      transform: "translateY(-50%)",
      color: "#9ca3af"
    },
    button: {
      width: "100%",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      padding: "16px",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px"
    },
    alert: {
      padding: "16px",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px"
    },
    error: { backgroundColor: "#fee2e2", color: "#dc2626" },
    success: { backgroundColor: "#dcfce7", color: "#16a34a" },
    backLink: {
      marginTop: "24px",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "6px",
      color: "#3b82f6",
      cursor: "pointer",
      fontWeight: "500"
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Reset Password</h1>
        <p style={styles.subtitle}>Enter your email to receive a reset link</p>

        {error && (
          <div style={{ ...styles.alert, ...styles.error }}>
            <AlertCircle style={{ width: "16px", height: "16px" }} />
            {error}
          </div>
        )}

        {success && (
          <div style={{ ...styles.alert, ...styles.success }}>
            <CheckCircle style={{ width: "16px", height: "16px" }} />
            {success}
          </div>
        )}

        <div style={styles.inputWrapper}>
          <Mail style={styles.inputIcon} />
          <input
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            disabled={loading}
          />
        </div>

        <button onClick={handleReset} style={styles.button} disabled={loading}>
          {loading ? "Sending..." : <>Send Reset Link <ArrowRight size={18} /></>}
        </button>

        <div style={styles.backLink} onClick={() => navigate("/login")}>
          <ArrowLeft size={16} /> Back to Login
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
